export class TikTokArchiverWidget extends HTMLElement {
    private baseApi = 'https://api-archiver.kaywat.codes';
    private username: string | null = null;
    private jobId: string | null = null;
    
    private actionArea!: HTMLDivElement;
    private statusEl!: HTMLDivElement;
    private progressBar!: HTMLDivElement;
    private progressFill!: HTMLDivElement;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.render();
    }

    static get observedAttributes() {
        return ['username'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'username' && newValue) {
            this.username = newValue.replace('@', '');
            this.startArchiving();
        }
    }

    render() {
        this.shadowRoot!.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    max-width: 500px;
                    margin: 10px 0;
                    font-family: var(--font-family, system-ui, -apple-system, sans-serif);
                }
                .widget-card {
                    background: #1e1f22;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }
                .header {
                    background: #000000;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }
                .logo {
                    width: 24px;
                    height: 24px;
                    background: #00f2fe;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 12px;
                }
                .title {
                    color: #fff;
                    font-weight: 600;
                    font-size: 14px;
                }
                .body {
                    padding: 16px;
                }
                .status-text {
                    color: #b5bac1;
                    font-size: 14px;
                    margin-bottom: 12px;
                }
                .progress-bar {
                    width: 100%;
                    height: 8px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 4px;
                    overflow: hidden;
                    display: none;
                    margin-bottom: 12px;
                }
                .progress-fill {
                    width: 0%;
                    height: 100%;
                    background: linear-gradient(90deg, #00f2fe, #4facfe);
                    transition: width 0.3s ease;
                }
                .action-area {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                    margin-bottom: 15px;
                }
                .stat-card {
                    background: rgba(255,255,255,0.05);
                    padding: 10px;
                    border-radius: 6px;
                    text-align: center;
                }
                .stat-val {
                    font-size: 16px;
                    font-weight: 600;
                    color: #fff;
                }
                .stat-lbl {
                    font-size: 12px;
                    color: #b5bac1;
                    margin-top: 4px;
                }
                .download-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    background: #000;
                    color: #fff;
                    border: 1px solid #00f2fe;
                    border-radius: 6px;
                    padding: 8px 16px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .download-btn:hover {
                    background: #00f2fe;
                    color: #000;
                }
            </style>
            <div class="widget-card">
                <div class="header">
                    <div class="logo"><i class="fab fa-tiktok" style="color: black;"></i></div>
                    <div class="title">TikTok Archiver</div>
                </div>
                <div class="body">
                    <div class="status-text" id="statusText">Waiting for username...</div>
                    <div class="progress-bar" id="progressBar">
                        <div class="progress-fill" id="progressFill"></div>
                    </div>
                    <div class="action-area" id="actionArea"></div>
                </div>
            </div>
        `;
        
        this.statusEl = this.shadowRoot!.getElementById('statusText') as HTMLDivElement;
        this.progressBar = this.shadowRoot!.getElementById('progressBar') as HTMLDivElement;
        this.progressFill = this.shadowRoot!.getElementById('progressFill') as HTMLDivElement;
        this.actionArea = this.shadowRoot!.getElementById('actionArea') as HTMLDivElement;
    }

    async startArchiving() {
        if (!this.username) return;

        this.statusEl.textContent = `Archiving @${this.username}...`;
        this.progressBar.style.display = 'block';
        this.progressFill.style.width = '10%';
        
        try {
            const res = await fetch(this.baseApi + '/scrape_archiver', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    username: this.username, 
                    max_videos: 0, 
                    include_playlists: false, 
                    include_reposts: false, 
                    include_favorites: false, 
                    include_liked: false 
                })
            });
            
            const data = await res.json();
            if (data.detail) throw new Error(data.detail);
            if (data.error) throw new Error(data.error);
            if (!data.job_id) throw new Error("No job ID returned.");
            
            this.jobId = data.job_id;
            this.pollStatus();
        } catch (err: any) {
            this.statusEl.textContent = 'Failed: ' + err.message;
            this.progressBar.style.display = 'none';
        }
    }

    async pollStatus() {
        if (!this.jobId) return;
        
        try {
            const res = await fetch(this.baseApi + '/status/' + this.jobId);
            const data = await res.json();
            
            if (data.status === 'processing' || data.status === 'starting' || data.status === 'pending' || data.status === 'running') {
                this.statusEl.textContent = data.progress_msg || 'Working...';
                if (data.progress) {
                    this.progressFill.style.width = Math.max(10, data.progress) + '%';
                }
                setTimeout(() => this.pollStatus(), 3000);
            } else if (data.status === 'completed') {
                this.progressFill.style.width = '100%';
                this.statusEl.textContent = 'Archive complete!';
                setTimeout(() => {
                    this.progressBar.style.display = 'none';
                    this.renderResult(data.result);
                }, 500);
            } else if (data.status === 'failed') {
                this.statusEl.textContent = 'Error: ' + (data.error || 'Unknown error');
                this.progressBar.style.display = 'none';
            } else if (data.status === 'cancelled') {
                this.statusEl.textContent = 'Cancelled.';
                this.progressBar.style.display = 'none';
            }
        } catch (err: any) {
            console.error(err);
            setTimeout(() => this.pollStatus(), 3000);
        }
    }

    renderResult(result: any) {
        if (!result) return;

        const vids = (result.videos_downloaded ?? result.total_videos) ?? '—';
        const zipSize = (result.zip_size_mb !== undefined && result.zip_size_mb !== null) ? `${result.zip_size_mb} MB` : '—';
        const dur = (result.duration_seconds !== undefined && result.duration_seconds !== null) ? `${result.duration_seconds}s` : '—';

        this.actionArea.innerHTML = `
            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-val">${vids}</div>
                    <div class="stat-lbl">Videos</div>
                </div>
                <div class="stat-card">
                    <div class="stat-val">${zipSize}</div>
                    <div class="stat-lbl">Archive Size</div>
                </div>
                <div class="stat-card">
                    <div class="stat-val">${dur}</div>
                    <div class="stat-lbl">Duration</div>
                </div>
                <div class="stat-card">
                    <div class="stat-val">✅</div>
                    <div class="stat-lbl">Status</div>
                </div>
            </div>
            <a class="download-btn" href="${this.baseApi}/download/${this.jobId}" target="_blank">
                <i class="fas fa-download"></i> Download ZIP Archive
            </a>
        `;
    }
}

export function initTikTokArchiverBridge() {
    if (!customElements.get('tiktok-archiver-widget')) {
        customElements.define('tiktok-archiver-widget', TikTokArchiverWidget);
    }
}
